<template>
  <div class="article-list-view">
    <section class="toolbar-card">
      <div>
        <h2>文章列表 Article List</h2>
        <p>维护 Knowledge 页面新闻文章、封面图、摘要和正文。</p>
      </div>
      <div class="toolbar-card__actions">
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="搜索标题 Search title"
          size="small"
          @keyup.enter.native="handleFilterChange"
          @clear="handleFilterChange"
        />
        <el-select v-model="statusFilter" clearable placeholder="状态 Status" size="small" @change="handleFilterChange">
          <el-option label="草稿 Draft" value="draft" />
          <el-option label="启用 Active" value="active" />
        </el-select>
        <el-button size="small" @click="handleFilterChange">查询 Search</el-button>
        <el-button type="primary" size="small" @click="openCreateDialog">新增文章 Add Article</el-button>
      </div>
    </section>

    <section class="table-card">
      <el-table :data="articles" v-loading="loading" stripe>
        <el-table-column label="封面 Cover" min-width="110">
          <template slot-scope="{ row }">
            <el-image v-if="row.coverImage" class="table-thumb" :src="assetUrl(row.coverImage)" :preview-src-list="[assetUrl(row.coverImage)]" :alt="row.title" fit="cover" />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题 Title" min-width="240" />
        <el-table-column prop="dateLabel" label="日期 Date" min-width="150" />
        <el-table-column label="排序 Sort" min-width="140">
          <template slot-scope="{ row }">
            <el-input-number
              class="sort-input"
              :value="row.sortOrder"
              :min="0"
              :step="1"
              size="mini"
              @change="value => updateArticleSortOrder(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="状态 Status" min-width="150">
          <template slot-scope="{ row }">
            <el-switch
              :value="row.status === 'active'"
              active-text="启用 Active"
              inactive-text="草稿 Draft"
              @change="value => toggleArticleStatus(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作 Actions" min-width="190" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="previewArticle(row)">预览 Preview</el-button>
            <el-button type="text" @click="openEditDialog(row)">编辑 Edit</el-button>
            <el-button type="text" class="danger-btn" @click="handleDelete(row)">删除 Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="pagination.total"
          @current-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </section>

    <el-dialog :title="dialogMode === 'create' ? '新增文章 Add Article' : '编辑文章 Edit Article'" :visible.sync="dialogVisible" width="820px" @closed="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <div class="form-grid">
          <el-form-item label="标题 Title" prop="title">
            <el-input v-model.trim="form.title" />
          </el-form-item>
          <el-form-item label="标识 Slug" prop="slug">
            <el-input v-model.trim="form.slug" @input="handleSlugInput" />
          </el-form-item>
        </div>

        <div class="form-grid">
          <el-form-item label="日期标签 Date Label">
            <el-input v-model.trim="form.dateLabel" placeholder="例如 April 28, 2026" />
          </el-form-item>
          <el-form-item label="状态 Status" prop="status">
            <el-select v-model="form.status" placeholder="请选择 Please select">
              <el-option label="草稿 Draft" value="draft" />
              <el-option label="启用 Active" value="active" />
            </el-select>
          </el-form-item>
        </div>

        <div class="form-grid">
          <el-form-item label="封面图 Cover Image">
            <div class="image-field">
              <el-input v-model.trim="form.coverImage" placeholder="图片地址 Image URL" />
              <el-upload action="" :http-request="request => uploadImage(request)" :show-file-list="false" accept="image/*">
                <el-button size="small" :loading="uploading">上传 Upload</el-button>
              </el-upload>
              <el-button size="small" @click="openMediaPicker">选择 Choose</el-button>
            </div>
            <el-image v-if="form.coverImage" class="image-preview" :src="assetUrl(form.coverImage)" :preview-src-list="[assetUrl(form.coverImage)]" fit="cover" />
          </el-form-item>
          <el-form-item label="排序 Sort Order">
            <el-input-number v-model="form.sortOrder" :min="0" :step="1" />
          </el-form-item>
        </div>

        <el-form-item label="摘要 Excerpt">
          <el-input v-model.trim="form.excerpt" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="正文 Content（每段之间换行 Separate paragraphs by line breaks）">
          <el-input v-model.trim="form.content" type="textarea" :rows="8" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消 Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">保存 Save</el-button>
      </span>
    </el-dialog>

    <media-picker-dialog
      :visible.sync="mediaPickerVisible"
      upload-module="article"
      @select="chooseMedia"
    />
  </div>
</template>

<script>
import { createArticle, deleteArticle, fetchArticles, updateArticle } from '../../api/articles'
import { resolveAssetUrl, uploadMediaAsset } from '../../api/media'
import MediaPickerDialog from '../../components/MediaPickerDialog.vue'
import { articlePreviewUrl, openPreviewUrl } from '../../utils/preview'
import { toSlug } from '../../utils/slug'

const createEmptyForm = () => ({
  id: null,
  title: '',
  slug: '',
  coverImage: '',
  dateLabel: '',
  excerpt: '',
  content: '',
  status: 'active',
  sortOrder: 0
})

export default {
  name: 'ArticleListView',
  components: {
    MediaPickerDialog
  },
  data () {
    return {
      loading: false,
      submitting: false,
      uploading: false,
      dialogVisible: false,
      mediaPickerVisible: false,
      dialogMode: 'create',
      slugTouched: false,
      keyword: '',
      statusFilter: '',
      articles: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      form: createEmptyForm(),
      rules: {
        title: [{ required: true, message: '请输入标题 Please input title.', trigger: 'blur' }],
        slug: [{ required: true, message: '请输入标识 Please input slug.', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态 Please select status.', trigger: 'change' }]
      }
    }
  },
  watch: {
    'form.title' (value) {
      if (!this.slugTouched) {
        this.form.slug = toSlug(value)
      }
    }
  },
  async created () {
    await this.loadData()
  },
  methods: {
    assetUrl: resolveAssetUrl,
    articleQueryParams () {
      return {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        keyword: this.keyword || undefined,
        status: this.statusFilter || undefined
      }
    },
    async loadData () {
      this.loading = true
      try {
        const { data } = await fetchArticles(this.articleQueryParams())
        this.articles = Array.isArray(data) ? data : data.items
        this.pagination.total = Array.isArray(data) ? data.length : data.total
      } catch (error) {
        this.$message.error('文章数据加载失败 Failed to load articles.')
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange (page) {
      this.pagination.page = page
      this.loadData()
    },
    handlePageSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.loadData()
    },
    openCreateDialog () {
      this.dialogMode = 'create'
      this.slugTouched = false
      this.dialogVisible = true
    },
    openEditDialog (row) {
      this.dialogMode = 'edit'
      this.slugTouched = true
      this.form = {
        id: row.id,
        title: row.title || '',
        slug: row.slug || '',
        coverImage: row.coverImage || '',
        dateLabel: row.dateLabel || '',
        excerpt: row.excerpt || '',
        content: row.content || '',
        status: row.status || 'draft',
        sortOrder: row.sortOrder || 0
      }
      this.dialogVisible = true
    },
    resetForm () {
      this.form = createEmptyForm()
      this.slugTouched = false
      if (this.$refs.form) this.$refs.form.resetFields()
    },
    handleSlugInput () {
      this.slugTouched = true
      this.form.slug = toSlug(this.form.slug)
    },
    openMediaPicker () {
      this.mediaPickerVisible = true
    },
    chooseMedia (item) {
      this.form.coverImage = item.fileUrl
      this.mediaPickerVisible = false
    },
    previewArticle (row) {
      openPreviewUrl(articlePreviewUrl(row))
    },
    async uploadImage ({ file }) {
      if (this.uploading) return
      this.uploading = true
      try {
        const { data } = await uploadMediaAsset(file, 'article')
        this.form.coverImage = data.fileUrl
        this.$message.success('图片已上传 Image uploaded.')
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '上传失败 Upload failed.')
      } finally {
        this.uploading = false
      }
    },
    handleSubmit () {
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        try {
          if (this.dialogMode === 'create') {
            await createArticle(this.form)
            this.$message.success('文章已创建 Article created.')
          } else {
            await updateArticle(this.form.id, this.form)
            this.$message.success('文章已更新 Article updated.')
          }
          this.dialogVisible = false
          await this.loadData()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '保存失败 Save failed.')
        } finally {
          this.submitting = false
        }
      })
    },
    buildArticlePayload (row, overrides = {}) {
      return {
        title: row.title,
        slug: row.slug,
        coverImage: row.coverImage || '',
        dateLabel: row.dateLabel || '',
        excerpt: row.excerpt || '',
        content: row.content || '',
        sortOrder: row.sortOrder || 0,
        status: row.status || 'draft',
        ...overrides
      }
    },
    async toggleArticleStatus (row, enabled) {
      const previousStatus = row.status
      const nextStatus = enabled ? 'active' : 'draft'
      row.status = nextStatus

      try {
        await updateArticle(row.id, this.buildArticlePayload(row, { status: nextStatus }))
        this.$message.success('状态已更新 Status updated.')
      } catch (error) {
        row.status = previousStatus
        this.$message.error((error.response && error.response.data && error.response.data.message) || '状态更新失败 Failed to update status.')
      }
    },
    async updateArticleSortOrder (row, value) {
      const previousSortOrder = row.sortOrder || 0
      const nextSortOrder = Number(value || 0)
      row.sortOrder = nextSortOrder

      try {
        await updateArticle(row.id, this.buildArticlePayload(row, { sortOrder: nextSortOrder }))
        this.$message.success('排序已更新 Sort order updated.')
      } catch (error) {
        row.sortOrder = previousSortOrder
        this.$message.error((error.response && error.response.data && error.response.data.message) || '排序更新失败 Failed to update sort order.')
      }
    },
    handleDelete (row) {
      this.$confirm(`确认删除文章“${row.title}”吗？\nConfirm deleting this article?`, '删除确认 Delete Confirmation', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteArticle(row.id)
          this.$message.success('文章已删除 Article deleted.')
          await this.loadData()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '删除失败 Delete failed.')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar-card,
.table-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(23, 20, 17, 0.04);
}

.toolbar-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
}

.toolbar-card h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
}

.toolbar-card p {
  margin: 8px 0 0;
  color: var(--muted);
}

.toolbar-card__actions,
.image-field,
.picker-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-card__actions {
  flex-shrink: 0;
}

.toolbar-card__actions .el-input,
.toolbar-card__actions .el-select {
  width: 190px;
}

.table-card {
  margin-top: 18px;
  padding: 12px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 4px 2px;
}

.table-thumb {
  display: block;
  width: 72px;
  height: 54px;
  object-fit: cover;
  border-radius: 6px;
  background: #f2eee8;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.sort-input {
  width: 112px;
}

.image-field .el-input {
  flex: 1;
}

.image-preview {
  display: block;
  width: 220px;
  max-width: 100%;
  aspect-ratio: 4 / 3;
  margin-top: 10px;
  object-fit: cover;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f2eee8;
}

.picker-toolbar {
  margin-bottom: 16px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  min-height: 260px;
}

.picker-card {
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  text-align: left;
  cursor: pointer;
}

.picker-card .el-image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.picker-card span {
  display: block;
  overflow: hidden;
  padding: 8px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.danger-btn {
  color: #d14343;
}

@media (max-width: 960px) {
  .toolbar-card,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-card__actions,
  .image-field {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
